import axios from 'axios';
import { STACItem, STACCollection, STACSearchResponse } from '../types/stac';
import { RWANDA_CONFIG } from '../config/rwanda';

export class STACApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = RWANDA_CONFIG.ENDPOINTS.stac;
  }

  async getCollections(): Promise<STACCollection[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/collections`, {
        timeout: 10000, // Add timeout
        headers: {
          'Accept': 'application/json'
        }
      });
      
      // Better validation
      if (!response.data || !Array.isArray(response.data.collections)) {
        console.warn('Invalid collections response format');
        return [];
      }
      
      return response.data.collections;
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      // Return empty array instead of throwing
      return [];
    }
  }

  async getCollection(collectionId: string): Promise<STACCollection> {
    try {
      const response = await axios.get(`${this.baseUrl}/collections/${collectionId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch collection ${collectionId}:`, error);
      throw new Error(`Failed to fetch collection: ${collectionId}`);
    }
  }

  async searchByBbox(
    bbox: [number, number, number, number],
    collections: string[] = [],
    datetime?: string,
    limit: number = 100
  ): Promise<STACSearchResponse> {
    try {
      const searchParams: any = {
        bbox,
        limit
      };

      if (collections.length > 0) {
        searchParams.collections = collections;
      }

      if (datetime) {
        searchParams.datetime = datetime;
      }

      const response = await axios.post(`${this.baseUrl}/search`, searchParams);
      return response.data;
    } catch (error) {
      console.error('STAC search failed:', error);
      throw new Error('STAC search failed');
    }
  }

  async searchByProvince(
    provinceName: string,
    collections: string[] = [],
    datetime?: string,
    limit: number = 100
  ): Promise<STACSearchResponse> {
    const province = RWANDA_CONFIG.PROVINCES.find(p => p.name === provinceName);
    if (!province) {
      throw new Error(`Province not found: ${provinceName}`);
    }

    return this.searchByBbox(province.bbox, collections, datetime, limit);
  }

  async searchByGeometry(
    geometry: any,
    collections: string[] = [],
    datetime?: string,
    limit: number = 100
  ): Promise<STACSearchResponse> {
    try {
      const searchParams: any = {
        intersects: geometry,
        limit
      };

      if (collections.length > 0) {
        searchParams.collections = collections;
      }

      if (datetime) {
        searchParams.datetime = datetime;
      }

      const response = await axios.post(`${this.baseUrl}/search`, searchParams);
      return response.data;
    } catch (error) {
      console.error('STAC geometry search failed:', error);
      throw new Error('STAC geometry search failed');
    }
  }

  buildDatetimeString(start: Date, end: Date): string {
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    return `${startIso}/${endIso}`;
  }

  async getItem(collectionId: string, itemId: string): Promise<STACItem> {
    try {
      const response = await axios.get(`${this.baseUrl}/collections/${collectionId}/items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch item ${itemId}:`, error);
      throw new Error(`Failed to fetch item: ${itemId}`);
    }
  }
}

export const stacApi = new STACApiService();